// src/contexts/AuthContext.jsx

import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { 
    auth, 
    db 
} from '../firebase/config';
import { 
    signInWithEmailAndPassword,
    signOut,
    setPersistence,
    browserLocalPersistence,
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    sendEmailVerification,
    sendPasswordResetEmail
} from 'firebase/auth';
import { 
    doc, 
    getDoc, 
    setDoc, 
    updateDoc,
    collection,
    query,
    where,
    getDocs,
    serverTimestamp 
} from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children, onUserVerified }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [emailVerified, setEmailVerified] = useState(false);
    const [sessionExpiryWarning, setSessionExpiryWarning] = useState(false);
    
    // Refs for cleanup
    const idleTimerRef = useRef();
    const sessionTimerRef = useRef();
    const warningTimerRef = useRef();
    const broadcastChannelRef = useRef();
    const activityListenersRef = useRef([]);

    // Login function - using session persistence
    const login = async (email, password) => {
        try {
            setError(null);
            // Set session persistence (cleared when tab/window is closed)
            await setPersistence(auth, browserLocalPersistence);
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            
            // Start session monitoring after successful login
            startSessionMonitoring();
            
            return userCredential.user;
        } catch (error) {
            setError(error.message);
            throw error;
        }
    };

    // Register function
    const register = async (email, password, userDetails) => {
        try {
            setError(null);
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Save user data to Firestore
            const userData = {
                fullName: userDetails.fullName,
                firstName: userDetails.fullName.split(' ')[0],
                lastName: userDetails.fullName.split(' ').slice(1).join(' '),
                email: user.email,
                phone: userDetails.phone,
                yearName: userDetails.yearName,
                dateJoined: userDetails.dateJoined,
                status: 'active',
                role: 'member',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                lastLogin: serverTimestamp(),
                emailVerified: false,
                welcomeEmailSent: false
            };

            await setDoc(doc(db, 'users', user.uid), userData);
            
            // Send email verification
            await sendEmailVerification(user);
            
            return user;
        } catch (error) {
            setError(error.message);
            throw error;
        }
    };

    // Send verification email
    const sendVerificationEmail = async () => {
        if (auth.currentUser) {
            await sendEmailVerification(auth.currentUser);
        }
    };

    // Check email verification status
    const checkEmailVerification = async () => {
        if (auth.currentUser) {
            await auth.currentUser.reload();
            const verified = auth.currentUser.emailVerified;
            setEmailVerified(verified);
            
            // If just verified and welcome email not sent, trigger callback
            if (verified && !userData?.welcomeEmailSent) {
                // Update Firestore to mark welcome email as sent
                await updateDoc(doc(db, 'users', auth.currentUser.uid), {
                    emailVerified: true,
                    emailVerifiedAt: serverTimestamp()
                });
                
                // Trigger the callback to send welcome email
                if (onUserVerified) {
                    onUserVerified(auth.currentUser, userData);
                }
            }
            
            return verified;
        }
        return false;
    };

    // Logout function with cleanup
    const logout = useCallback(async (reason = 'user') => {
        try {
            // Clear all timers
            clearAllTimers();
            
            // Remove activity listeners
            removeActivityListeners();
            
            // Broadcast logout to other tabs
            if (broadcastChannelRef.current) {
                broadcastChannelRef.current.postMessage({ 
                    type: "logout", 
                    reason: reason 
                });
            }
            
            // Sign out from Firebase
            await signOut(auth);
            
            // Clear state
            setCurrentUser(null);
            setUserData(null);
            setIsAdmin(false);
            setEmailVerified(false);
            setSessionExpiryWarning(false);
            
            console.log(`Logged out due to: ${reason}`);
        } catch (error) {
            setError(error.message);
            throw error;
        }
    }, []);

    // Clear all timers helper
    const clearAllTimers = () => {
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
            idleTimerRef.current = null;
        }
        if (sessionTimerRef.current) {
            clearTimeout(sessionTimerRef.current);
            sessionTimerRef.current = null;
        }
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
            warningTimerRef.current = null;
        }
    };

    // Remove activity listeners
    const removeActivityListeners = () => {
        if (activityListenersRef.current.length > 0) {
            activityListenersRef.current.forEach(({ event, handler }) => {
                window.removeEventListener(event, handler);
            });
            activityListenersRef.current = [];
        }
    };

    // Reset idle timer on user activity
    const resetIdleTimer = useCallback(() => {
        if (!currentUser) return;

        // Clear existing idle timer
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
        }

        // Clear warning timer if it exists
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
            setSessionExpiryWarning(false);
        }

        // Set new idle timer for 30 minutes
        idleTimerRef.current = setTimeout(() => {
            // Show warning 1 minute before logout (at 29 minutes)
            setSessionExpiryWarning(true);
            
            warningTimerRef.current = setTimeout(() => {
                logout('idle timeout');
            }, 60 * 1000); // 1 minute warning
        }, 29 * 60 * 1000); // 29 minutes
    }, [currentUser, logout]);

    // Setup activity listeners
    const setupActivityListeners = useCallback(() => {
        // Events to listen for user activity
        const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
        
        // Remove existing listeners
        removeActivityListeners();

        // Add new listeners
        events.forEach(event => {
            const handler = () => resetIdleTimer();
            window.addEventListener(event, handler);
            activityListenersRef.current.push({ event, handler });
        });

        // Initial timer setup
        resetIdleTimer();
    }, [resetIdleTimer]);

    // Start session monitoring
    const startSessionMonitoring = useCallback(() => {
        if (!currentUser) return;
        
        // Setup activity listeners for idle timeout
        setupActivityListeners();
        
        // Additional session check - monitor if tab is becoming hidden
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Tab is hidden, but we don't logout immediately
                // Just clear timers to prevent false timeouts
                clearAllTimers();
            } else {
                // Tab is visible again, restart monitoring
                if (currentUser) {
                    resetIdleTimer();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Store for cleanup
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [currentUser, setupActivityListeners, resetIdleTimer]);

    // Password reset
    const resetPassword = async (email) => {
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (error) {
            setError(error.message);
            throw error;
        }
    };

    // Load user data from Firestore
    const loadUserData = async (user) => {
        try {
            let roleCollection = 'users';
            let data = null;

            // Check admins first
            const adminDoc = await getDoc(doc(db, 'admins', user.uid));

            if (adminDoc.exists()) {
                data = adminDoc.data();
                roleCollection = 'admins';
                setIsAdmin(true);
                setEmailVerified(user.emailVerified);
            } else {
                // Check users collection
                const userDoc = await getDoc(doc(db, 'users', user.uid));

                if (userDoc.exists()) {
                    data = userDoc.data();
                    roleCollection = 'users';
                    setIsAdmin(false);
                    setEmailVerified(user.emailVerified || data.emailVerified || false);
                } else {
                    // Only create if NOT admin and NOT existing user
                    data = {
                        email: user.email,
                        firstName: user.displayName?.split(' ')[0] || 'User',
                        lastName: user.displayName?.split(' ')[1] || '',
                        fullName: user.displayName || 'User',
                        createdAt: serverTimestamp(),
                        lastLogin: serverTimestamp(),
                        status: 'active',
                        emailVerified: user.emailVerified,
                        welcomeEmailSent: false
                    };

                    await setDoc(doc(db, 'users', user.uid), data);
                    roleCollection = 'users';
                    setIsAdmin(false);
                    setEmailVerified(user.emailVerified);
                }
            }

            setUserData(data);

            // Update last login in the correct collection
            await updateDoc(doc(db, roleCollection, user.uid), {
                lastLogin: serverTimestamp()
            });

            await checkEmailVerification();

        } catch (error) {
            console.error('Error loading user data:', error);
            setError(error.message);
        }
    };

    // Handle before unload (tab closing)
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (currentUser) {
                // We don't actually logout here because Firebase session persistence
                // will handle it (browserSessionPersistence clears on tab close)
                // But we can broadcast to other tabs if needed
                if (broadcastChannelRef.current) {
                    broadcastChannelRef.current.postMessage({ 
                        type: "tab_closing",
                        userId: currentUser.uid 
                    });
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [currentUser]);

    // Auth state observer
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
    try {
        if (user) {
            setCurrentUser(user);
            await loadUserData(user);
            startSessionMonitoring();
        } else {
            setCurrentUser(null);
            setUserData(null);
            setIsAdmin(false);
            setEmailVerified(false);
            setSessionExpiryWarning(false);
            clearAllTimers();
            removeActivityListeners();
        }
    } catch (error) {
        console.error(error);
    } finally {
        // ✅ ONLY here
        setLoading(false);
    }
});
        return () => {
            unsubscribe();
            clearAllTimers();
            removeActivityListeners();
        };
    }, [startSessionMonitoring]);

    // Setup broadcast channel for cross-tab communication
    useEffect(() => {
        // Create broadcast channel
        broadcastChannelRef.current = new BroadcastChannel("auth");

        // Listen for messages from other tabs
        broadcastChannelRef.current.onmessage = (event) => {
            const { type, reason, userId } = event.data;
            
            if (type === "logout" && currentUser) {
                console.log(`Logging out due to ${reason || 'logout'} in another tab`);
                logout(reason || 'cross-tab');
            } else if (type === "tab_closing" && currentUser && userId !== currentUser.uid) {
                // Another tab is closing, but we don't need to do anything
                // Just for logging/monitoring purposes
                console.log('Another tab closed');
            }
        };

        // Cleanup
        return () => {
            if (broadcastChannelRef.current) {
                broadcastChannelRef.current.close();
            }
        };
    }, [currentUser, logout]);

    const value = {
        currentUser,
        userData,
        isAdmin,
        loading,
        error,
        emailVerified,
        sessionExpiryWarning,
        login,
        register,
        logout,
        resetPassword,
        sendVerificationEmail,
        checkEmailVerification,
        resetIdleTimer // Expose this to allow manual reset if needed
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
